import { jsx as _jsx } from "react/jsx-runtime";
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { parse, startOfDay } from 'date-fns';
import { DatePickerFooter } from 'syf-component-library/ui/patterns/DatePicker';
import { formatDateObjectToString, formatDateStringToObject } from 'syf-js-utilities/formats';
import { FAL_CALENDAR_EXCLAMATION, FAR_CALENDAR_DAYS } from '../../../../const/iconProps';
import { PINLESS_DEBIT_CARD } from '../../../../const/paymentMedium';
import { getDaysAllowedInFuture, getEndDateForPayment, getEndDateFromDaysAllowedInFuture, getEstDateObject, isPayLaterProduct } from '../../../../helpers';
import isDCHEligible from '../../../../helpers/isDCHEligible';
import useApiMachine from '../../../../hooks/machines/useApiMachine';
import usePaymentFlowMachine from '../../../../hooks/machines/usePaymentFlowMachine';
import { getPaymentOptionAndMethod } from '../../../../machines/helpers';
import { PAYMENT_DATE_INPUT_NAME } from '../../../../machines/oneTimePayment/paymentDateConfig';
import { t } from '../../../../strings/i18n';
import { useField } from '../../../../xform';
import { PaymentDatePicker, SelectWrapper } from '../subcomponents';
import { getHighlightedDates } from './helpers';
export const CalendarIcon = styled(FontAwesomeIcon) `
  padding: 1px 4px;
`;
const getEndDate = (dchEligible, product, daysDelinquent, paymentEnterpriseEligibility) => {
    /*
     * TODO: ideally would love for this new daysAllowedInFuture attribute to be the source of truth
     * for every product and permutation moving forward but for now it's scoped to just CAP and Settlement
     */
    if (dchEligible) {
        const daysAllowedInFuture = getDaysAllowedInFuture(paymentEnterpriseEligibility);
        if (!Number.isNaN(daysAllowedInFuture) && daysAllowedInFuture >= 0)
            return getEndDateFromDaysAllowedInFuture(daysAllowedInFuture);
    }
    return getEndDateForPayment(daysDelinquent, product);
};
const PaymentDateSelect = ({ id }) => {
    const [, paymentFlowMachine] = usePaymentFlowMachine();
    const paymentDate = useField(PAYMENT_DATE_INPUT_NAME);
    const [apiMachine] = useApiMachine();
    const { accountSummaryQueryResult, alternatePaymentMethodsQueryResult, paymentEnterpriseEligibilityQueryResult, paymentMethodsQueryResult, featureFlags } = apiMachine.context;
    const { data: accountSummary } = accountSummaryQueryResult;
    const { data: paymentMethodData } = paymentMethodsQueryResult;
    const { data: alternatePaymentMethodsData } = alternatePaymentMethodsQueryResult;
    const { billing, product } = accountSummary;
    const { type: productType } = product;
    const { daysDelinquent, nextPaymentDate: dueDate } = billing;
    const { data: paymentEnterpriseEligibility } = paymentEnterpriseEligibilityQueryResult;
    const dchEligible = isDCHEligible(billing, productType, featureFlags);
    const [, paymentMethod] = getPaymentOptionAndMethod(paymentFlowMachine.context.form, alternatePaymentMethodsData, paymentMethodData);
    const isDebitCardSelected = (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.paymentMedium) === PINLESS_DEBIT_CARD;
    const isDCHDebitCardSelected = dchEligible && isDebitCardSelected;
    const isProductInstallmentEnterprise = isPayLaterProduct(productType);
    const defaultHelperText = t('label.oneTimePaymentDateSelectHelpText.default');
    const secondaryHelperText = isDCHDebitCardSelected
        ? t('label.oneTimePaymentDateSelectHelpText.dchDebitCard')
        : '';
    const helperText = `${secondaryHelperText} ${defaultHelperText}`.trim();
    const datepickerEndDate = getEndDate(dchEligible, product, daysDelinquent, paymentEnterpriseEligibility);
    const handleDateChange = (date) => {
        if (date === null || date === void 0 ? void 0 : date.toJSON()) {
            paymentDate.setValue(formatDateObjectToString(startOfDay(date)));
            paymentDate.blur();
            return;
        }
        paymentDate.setValue(null);
        paymentDate.blur();
    };
    return (_jsx(SelectWrapper, Object.assign({ "data-scroll": PAYMENT_DATE_INPUT_NAME, horizontal: "medium" }, { children: _jsx(PaymentDatePicker, Object.assign({ boundarySelector: "#make-a-payment", calendarIcon: paymentDate.error ? (_jsx(CalendarIcon, { icon: FAL_CALENDAR_EXCLAMATION, size: "lg" })) : (_jsx(CalendarIcon, { icon: FAR_CALENDAR_DAYS, size: "lg" })), className: "PaymentDatePicker", endDate: datepickerEndDate, error: paymentDate.error, handleOnChange: handleDateChange, helpText: helperText, highlightDateThemeArr: getHighlightedDates(featureFlags, billing.pastDue, dueDate), idTextField: id, isReadOnly: isProductInstallmentEnterprise || isDCHDebitCardSelected, name: id, placeholderLabel: t('makePayment.paymentDate'), required: true, selectedDate: paymentDate.value ? formatDateStringToObject(paymentDate.value) : null, showMonthYearDropdown: true, startDate: startOfDay(getEstDateObject(new Date())), submitButtonCustomText: t('makePayment.submitButtonText'), onBlurHandler: (date) => {
                handleDateChange(parse(date, 'MM/dd/yyyy', new Date()));
            } }, { children: _jsx(DatePickerFooter, { bottomDisclaimerCopy: t('makePayment.makePaymentDisclaimer'), selectedDateText: t('makePayment.selectedPaymentDate'), showLegend: true }) })) })));
};
export default PaymentDateSelect;
